import type { I18n, IntlDateTimeFormats } from 'vue-i18n';
import type { ThemeDefinition } from 'vuetify';
import { useI18n } from 'vue-i18n';
import { createVuetify } from 'vuetify';
import { VAlert, VApp, VBtn, VContainer, VFadeTransition, VMain, VToolbar, VToolbarTitle } from 'vuetify/components';
import * as directives from 'vuetify/directives';
import { aliases, fa } from 'vuetify/iconsets/fa';
import { createVueI18nAdapter } from 'vuetify/locale/adapters/vue-i18n';
import colors from 'vuetify/util/colors';

import 'vuetify/styles';
import '@fontsource/roboto/100.css';
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import '@fontsource/roboto/900.css';

/* optional italic styles */
import '@fontsource/roboto/100-italic.css';
import '@fontsource/roboto/300-italic.css';
import '@fontsource/roboto/400-italic.css';
import '@fontsource/roboto/500-italic.css';
import '@fontsource/roboto/700-italic.css';
import '@fontsource/roboto/900-italic.css';
import '../sass/app.scss';

const commonColors: Partial<ThemeDefinition['colors']> = {
  'font': colors.grey.darken2,
  'store': '#e9a013',
  'unacknowledged': colors.grey.lighten3,
  'light-grey': colors.grey.lighten4,
  'dark-grey': colors.grey.darken1,
  'error': colors.red.base,
  'warning': colors.orange.darken1,
  'success': colors.green.base,
};

// Default theme
const tealTheme: ThemeDefinition = {
  colors: {
    'primary': '#02979D',
    'primary-darken-1': colors.cyan.darken4,
    'primary-lighten-1': '#ebfeff',
    'on-primary-lighten-1': colors.cyan.darken4,
    'secondary': '#FF8811',
    ...commonColors,
  },
};

const sapphireTheme: ThemeDefinition = {
  colors: {
    'primary': '#428dcb',
    'primary-darken-1': '#06367c',
    'primary-lighten-1': '#e3f2fd',
    'secondary': '#ff8811',
    ...commonColors,
  },
};

const purpleTheme: ThemeDefinition = {
  colors: {
    'primary': '#7E57C2',
    'primary-darken-1': '#4527a0',
    'primary-lighten-1': '#EDE7F6',
    'secondary': '#ff8811',
    ...commonColors,
  },
};

const graphiteTheme: ThemeDefinition = {
  colors: {
    'primary': '#717684',
    'primary-darken-1': '#44444c',
    'primary-lighten-1': '#eff0f1',
    'secondary': '#ff8811',
    ...commonColors,
  },
};

const emeraldTheme: ThemeDefinition = {
  colors: {
    'primary': '#399381',
    'primary-darken-1': '#31574f',
    'primary-lighten-1': '#d3eee8',
    'secondary': '#ff8811',
    ...commonColors,
  },
};

const rubyTheme: ThemeDefinition = {
  colors: {
    'primary': '#781e4c',
    'primary-darken-1': '#46162e',
    'primary-lighten-1': '#f5e0ea',
    'secondary': '#ff8811',
    ...commonColors,
  },
};

const blueTheme: ThemeDefinition = {
  colors: {
    'primary': '#1c5c9c',
    'primary-darken-1': '#103152',
    'primary-lighten-1': '#DCEAF9',
    'secondary': '#ff8811',
    ...commonColors,
  },
};

const sodaliteTheme: ThemeDefinition = {
  colors: {
    'primary': '#327d9a',
    'primary-darken-1': '#225569',
    'primary-lighten-1': '#f0f7fa',
    'secondary': '#ff8811',
    ...commonColors,
  },
};

export function initVuetify(
  i18n: I18n<Record<string, unknown>, IntlDateTimeFormats, Record<string, unknown>, string, false>,
) {
  return createVuetify({
    locale: {
      adapter: createVueI18nAdapter({ i18n, useI18n }),
    },
    defaults: {
      VAlert: { variant: 'tonal', icon: '' },
      VBtn: {
        size: 'large',
        style: 'letter-spacing: 0',
      },
      VCard: {
        VCardActions: {
          VBtn: { variant: 'elevated' },
        },
      },
      VDataTable: { density: 'comfortable', striped: 'odd' },
      VDataTableServer: { striped: 'odd' },
      VBtnGroup: { density: 'compact' },
      VCheckbox: {
        density: 'compact',
        color: 'primary',
        style: 'height: 45px',
      },
      VCombobox: {
        variant: 'outlined',
        density: 'compact',
        color: 'primary',
      },
      VDivider: { thickness: '2', color: 'grey-lighten-1' },
      VDialog: { retainFocus: false },
      VTextField: {
        variant: 'outlined',
        density: 'compact',
        color: 'primary',
      },
      VTextarea: {
        variant: 'outlined',
        density: 'compact',
        color: 'primary',
      },
      VFileInput: {
        variant: 'outlined',
        density: 'compact',
        color: 'primary',
      },
      VRadioGroup: { density: 'compact', color: 'primary' },
      VRadio: { density: 'compact', color: 'primary' },
      VNavBar: { touchless: true },
      VAutocomplete: { variant: 'outlined', density: 'compact' },
      VPagination: { color: 'primary', density: 'compact' },
      VSelect: { variant: 'outlined', density: 'compact', color: 'primary' },
      VSwitch: {
        color: 'primary',
        density: 'compact',
        style: 'height: 35px',
      },
      VTabs: {
        'grow': true,
        'class': 'global-tabs border-b-md mb-4',
        'show-arrows': true,
      },
    },
    // Only what the Blade layouts use: those in-DOM templates are not seen by
    // vite-plugin-vuetify's autoImport, which handles every SFC.
    components: {
      VAlert,
      VApp,
      VBtn,
      VContainer,
      VFadeTransition,
      VMain,
      VToolbar,
      VToolbarTitle,
    },
    directives,
    icons: {
      defaultSet: 'fa',
      aliases,
      sets: {
        fa,
      },
    },
    theme: {
      defaultTheme: 'sapphireTheme',
      themes: {
        sapphireTheme,
        tealTheme,
        graphiteTheme,
        purpleTheme,
        sodaliteTheme,
        blueTheme,
        rubyTheme,
        emeraldTheme,
        light: {
          dark: false,
        },
      },
    },
  });
}
